import { useCallback, useMemo } from 'react';

import { type EAccessCheckTrackingStatus } from '@/packages/EarlyAccessPaywall/shared/components/Provider/Provider';
import { useUserIsAgentSelector } from '@/packages/redux-state/store/slices/user';

import { useCAForCard } from '../../../hooks/card/useCAForCard';

const CATEGORY = 'Phones';

type TTracking = {
  trackEarlyAccessConfirmed: VoidFunction;
  trackEarlyAccessBlocked: (trackingStatus: EAccessCheckTrackingStatus) => void;
  trackPhoneButtonClick: VoidFunction;
};

export const usePhoneTracking = (): TTracking => {
  const ca = useCAForCard();

  const isAgent = useUserIsAgentSelector();

  const trackEarlyAccessConfirmed = useCallback(() => {
    ca({ category: CATEGORY, action: 'Open_listing_in_map_early_access' }, { label: true });
  }, [ca]);

  const trackEarlyAccessBlocked = useCallback(
    (trackingStatus: EAccessCheckTrackingStatus) => {
      ca(
        { category: CATEGORY, action: 'Blocked_listing_in_map_early_access' },
        { label: true, pageExtra: { block_type: trackingStatus } },
      );
    },
    [ca],
  );

  const trackPhoneButtonClick = useCallback(() => {
    ca({ category: CATEGORY, action: isAgent ? 'Open_listing_agent_block' : 'Open_listing' }, { label: true });
  }, [ca, isAgent]);

  return useMemo(
    () => ({
      trackEarlyAccessConfirmed,
      trackEarlyAccessBlocked,
      trackPhoneButtonClick,
    }),
    [trackEarlyAccessConfirmed, trackEarlyAccessBlocked, trackPhoneButtonClick],
  );
};
